import { getCashDifferencesSummary } from './cashSummaryQuery';
import { fetchRemoteCashClosuresHistory } from '../../reports/infra/reportsRemoteRepo';
import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { CASH_DIFFERENCES_PAGE_SIZE } from './cashDifferencesConstants';

export type CashDifferenceRow = {
  id: string;
  closedAt: string;
  expectedAmount: number;
  closingAmount: number;
  difference: number;
  note?: string | null;
};

export type ResolveCashDifferencesOpts = {
  lastDays?: number;
  limit?: number;
  offset?: number;
};

type CashDifferencesRemoteSession = {
  id: string;
  openedAt: string;
  closedAt?: string | null;
  expectedAmount?: number | null;
  closingAmount?: number | null;
  difference?: number | null;
  note?: string | null;
};

function mapCashSessionToDifferenceRow(row: CashDifferencesRemoteSession): CashDifferenceRow {
  return {
    id: row.id,
    closedAt: row.closedAt ?? row.openedAt,
    expectedAmount: row.expectedAmount ?? 0,
    closingAmount: row.closingAmount ?? 0,
    difference: row.difference ?? 0,
    note: row.note,
  };
}

/** @internal tests */
export async function resolveCashDifferencesPageWithDeps(
  branchId: string,
  opts: ResolveCashDifferencesOpts | undefined,
  deps: {
    isOnline: () => Promise<boolean>;
    fetchRemote: (
      branchId: string,
      opts: {
        fromIso: string;
        toIso: string;
        limit: number;
        offset: number;
        differenceFilter: 'any';
        sort: 'closed_desc';
      }
    ) => Promise<CashDifferencesRemoteSession[] | null>;
    getLocal: (
      branchId: string,
      opts: { lastDays: number; limit: number; offset: number }
    ) => Promise<CashDifferenceRow[]>;
  }
): Promise<CashDifferenceRow[]> {
  const lastDays = opts?.lastDays ?? 7;
  const limit = opts?.limit ?? CASH_DIFFERENCES_PAGE_SIZE;
  const offset = opts?.offset ?? 0;

  if (await deps.isOnline()) {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - lastDays);
    fromDate.setHours(0, 0, 0, 0);

    const remote = await deps.fetchRemote(branchId, {
      fromIso: fromDate.toISOString(),
      toIso: new Date().toISOString(),
      limit,
      offset,
      differenceFilter: 'any',
      sort: 'closed_desc',
    });
    if (remote) {
      return remote.map(mapCashSessionToDifferenceRow);
    }
  }

  return deps.getLocal(branchId, { lastDays, limit, offset });
}

export { mapCashSessionToDifferenceRow };

export async function resolveCashDifferencesPage(
  branchId: string,
  opts?: ResolveCashDifferencesOpts
): Promise<CashDifferenceRow[]> {
  return resolveCashDifferencesPageWithDeps(branchId, opts, {
    isOnline: isOnline,
    fetchRemote: fetchRemoteCashClosuresHistory,
    getLocal: getCashDifferencesSummary,
  });
}
